import { useTranslation } from 'react-i18next'
import { Icon } from '../icon/icon'
import Text from '../text/text'
import { NavLink } from '~/components/internal-nav-link/Internal-nav-link'
import { useOptionalUser } from '~/utils/user'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '../ui/sheet'
import { ThemeSwitch } from '../theme-switch/theme-switch'
import LanguageSwitch from '../language-switch/language-switch'
import { getImgSrc } from '~/utils/image'
import LoginModal from '../login-modal/login-modal'
import { useState } from 'react'

interface MenuSheetProps {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	trigger: React.ReactNode
	isMobile?: boolean
}

export function MenuSheet({
	isOpen,
	onOpenChange,
	trigger,
	isMobile = false,
}: MenuSheetProps) {
	const user = useOptionalUser()
	const isAdmin = user?.roles.map(x => x.name).includes('admin') || false
	const { t } = useTranslation()

	const [showLoginModal, setShowLoginModal] = useState(false)
	const [loginModalInitialPage, setLoginModalInitialPage] = useState(1)

	const toggleShowLoginModal = (initialPage: number = 0) => {
		setLoginModalInitialPage(initialPage)
		setShowLoginModal(!showLoginModal)
	}

	const openLoginModal = (view: 'login' | 'signup') => {
		closeSheet()
		setLoginModalInitialPage(view == 'login' ? 0 : 2)
		setShowLoginModal(!showLoginModal)
	}

	const closeSheet = () => onOpenChange(false)

	const menuItemClasses =
		'flex w-full flex-row items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-accent hover:text-accent-foreground border-b border-border last:border-b-0'
	const lockedMenuItemClasses =
		'flex w-full flex-row items-center justify-between gap-3 px-4 py-3 text-sm transition-colors hover:bg-accent hover:text-accent-foreground border-b border-border last:border-b-0'

	return (
		<>
			{showLoginModal && (
				<LoginModal
					toggleLoginModal={toggleShowLoginModal}
					initialPage={loginModalInitialPage}
				/>
			)}
			<Sheet open={isOpen} onOpenChange={onOpenChange}>
				<SheetTrigger asChild>{trigger}</SheetTrigger>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>
							{user && (
								<div className="flex items-center gap-3">
									<div className="bg-muted flex h-10 w-10 items-center justify-center rounded-full">
										{user.imageId ? (
											<img
												alt={user.name ?? ''}
												src={getImgSrc(user.imageId, 'user')}
												loading="lazy"
												className="h-10 w-10 rounded-full object-cover"
											/>
										) : (
											<Icon
												size="md"
												name="avatar"
												className="text-muted-foreground"
											/>
										)}
									</div>
									<div className="flex flex-col">
										<Text className="font-medium">{user.name}</Text>
										<Text as="sm" className="text-muted-foreground">
											{user.email}
										</Text>
									</div>
								</div>
							)}
						</SheetTitle>
					</SheetHeader>
					<div className="flex h-full flex-col">
						<div className="flex-1 p-2">
							{user ? (
								<>
									{/* All menu items for logged in users */}
									<NavLink
										className={menuItemClasses}
										to="/profile"
										onClick={closeSheet}
									>
										<Icon size="sm" name="avatar" />
										<Text>{t('misc_profile')}</Text>
									</NavLink>
									<NavLink
										className={menuItemClasses}
										to="/favorites"
										onClick={closeSheet}
									>
										<Icon size="sm" name="heart" />
										<Text>{t('misc_favorites')}</Text>
									</NavLink>
									<NavLink
										className={menuItemClasses}
										to="/profile/interests"
										onClick={closeSheet}
									>
										<Icon size="sm" name="ticket" />
										<Text>{t('interests_title')}</Text>
									</NavLink>
									<NavLink
										className={menuItemClasses}
										to="/my-events"
										onClick={closeSheet}
									>
										<Icon size="sm" name="calendar" />
										<Text>{t('profile_my_events')}</Text>
									</NavLink>
									{isAdmin && (
										<>
											<NavLink
												className={menuItemClasses}
												to="/profile/statistics"
												onClick={closeSheet}
											>
												<Icon size="sm" name="bar-chart" />
												<Text>{t('misc_statistics')}</Text>
											</NavLink>
											<NavLink
												className={menuItemClasses}
												to="/admin"
												onClick={closeSheet}
											>
												<Icon size="sm" name="lock" />
												<Text>{t('misc_admin')}</Text>
											</NavLink>
										</>
									)}
								</>
							) : (
								<>
									{/* All menu items for logged out users */}
									{isMobile ? (
										<NavLink
											className={menuItemClasses}
											to="/login"
											onClick={closeSheet}
										>
											<Icon size="sm" name="avatar" />
											<Text>{t('action_login')}</Text>
										</NavLink>
									) : (
										<button
											className={menuItemClasses}
											onClick={() => openLoginModal('login')}
										>
											<Icon size="sm" name="avatar" />
											<Text>{t('action_login')}</Text>
										</button>
									)}
									{isMobile ? (
										<NavLink
											className={menuItemClasses}
											to="/signup"
											onClick={closeSheet}
										>
											<Icon size="sm" name="user-group" />
											<Text>{t('action_create_account')}</Text>
										</NavLink>
									) : (
										<button
											className={menuItemClasses}
											onClick={() => openLoginModal('signup')}
										>
											<Icon size="sm" name="user-group" />
											<Text>{t('action_create_account')}</Text>
										</button>
									)}
									{isMobile ? (
										<NavLink
											className={lockedMenuItemClasses}
											to="/favorites"
											onClick={closeSheet}
										>
											<div className="flex items-center gap-3">
												<Icon size="sm" name="heart" />
												<Text>{t('misc_favorites')}</Text>
											</div>
											<Icon
												size="xs"
												name="lock"
												className="text-muted-foreground"
											/>
										</NavLink>
									) : (
										<button
											className={lockedMenuItemClasses}
											onClick={() => openLoginModal('login')}
										>
											<div className="flex items-center gap-3">
												<Icon size="sm" name="heart" />
												<Text>{t('misc_favorites')}</Text>
											</div>
											<Icon
												size="xs"
												name="lock"
												className="text-muted-foreground"
											/>
										</button>
									)}
									{isMobile ? (
										<NavLink
											className={lockedMenuItemClasses}
											to="/profile/interests"
											onClick={closeSheet}
										>
											<div className="flex items-center gap-3">
												<Icon size="sm" name="ticket" />
												<Text>{t('interests_title')}</Text>
											</div>
											<Icon
												size="xs"
												name="lock"
												className="text-muted-foreground"
											/>
										</NavLink>
									) : (
										<button
											className={lockedMenuItemClasses}
											onClick={() => openLoginModal('login')}
										>
											<div className="flex items-center gap-3">
												<Icon size="sm" name="ticket" />
												<Text>{t('interests_title')}</Text>
											</div>
											<Icon
												size="xs"
												name="lock"
												className="text-muted-foreground"
											/>
										</button>
									)}
									{isMobile ? (
										<NavLink
											className={lockedMenuItemClasses}
											to="/my-events/new"
											onClick={closeSheet}
										>
											<div className="flex items-center gap-3">
												<Icon size="sm" name="plus" />
												<Text>{t('events_create_new')}</Text>
											</div>
											<Icon
												size="xs"
												name="lock"
												className="text-muted-foreground"
											/>
										</NavLink>
									) : (
										<button
											className={lockedMenuItemClasses}
											onClick={() => openLoginModal('login')}
										>
											<div className="flex items-center gap-3">
												<Icon size="sm" name="plus" />
												<Text>{t('events_create_new')}</Text>
											</div>
											<Icon
												size="xs"
												name="lock"
												className="text-muted-foreground"
											/>
										</button>
									)}
									<NavLink
										className={menuItemClasses}
										to="/info"
										onClick={closeSheet}
									>
										<Icon size="sm" name="exclamation-circle" />
										<Text>{t('info_how_it_works')}</Text>
									</NavLink>
								</>
							)}
						</div>
						{/* Settings at bottom */}
						<div className="border-border bg-muted/20 space-y-3 border-t px-4 py-4">
							<Text
								as="sm"
								className="text-muted-foreground font-semibold tracking-wide uppercase"
							>
								{t('misc_settings')}
							</Text>
							<div className="space-y-2">
								<ThemeSwitch mobile />
								<LanguageSwitch mobile />
							</div>
							{user && (
								<div className="border-border/50 border-t pt-2">
									<NavLink
										className="text-muted-foreground hover:text-destructive hover:bg-destructive/5 flex items-center gap-2 rounded-md px-2 py-2 text-xs transition-colors"
										to="/logout"
										onClick={closeSheet}
									>
										<Icon size="xs" name="exit" />
										<Text as="xs">{t('action_logout')}</Text>
									</NavLink>
								</div>
							)}
						</div>
					</div>
				</SheetContent>
			</Sheet>
		</>
	)
}
